// Components==============
import deleteFeedbackAction from 'actions/feedback/destroy';
import storeFeedback from 'actions/feedback/store';
import { useRedux } from 'hooks/useRedux';
import { useReduxAction } from 'hooks/useReduxAction';
import React, { useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { EvidenceState } from 'typings';
import { useTranslate } from 'hooks/useTranslate';
import ImageManager from '../ImageManager/ImageManager';
import Message from '../Message/Message';
import MessageBar from '../MessageBar/MessageBar';
import Modal from '../Modal/Modal';
import './EvidenceModal.module.scss';

// =========================

type Props = {
  evidence: EvidenceEntity[];
  handleDelete?: (c: EvidenceState) => void;
  deleteCondition?: boolean;
  initialImage?: number;
  disableComments?: boolean;
  canDeleteIfUserCreated?: boolean;
  disableMessageBar?: boolean;
  setModalIsOpen?: React.Dispatch<React.SetStateAction<boolean>>;
};

const ProjectEvidence = ({ evidence }: { evidence: EvidenceEntity[] }) => {
  const translate = useTranslate();
  const process = useRedux(
    (state) => state.process?.entities.byId[evidence[0].process_id]
  );

  const activity = useRedux((state) =>
    process?.process_id
      ? state.activity?.entities.byId[process?.process_id]
      : undefined
  );

  const project = useRedux((state) =>
    process?.project_id
      ? state.project?.entities.byId?.[process?.project_id]
      : undefined
  );

  const theme = useRedux((state) =>
    project?.theme_id
      ? state.theme?.entities.byId[project?.theme_id]
      : undefined
  );

  return (
    <div styleName="info">
      <h1>{activity?.name}</h1>
      <NavLink to={`/werkplaats/project-details/${project?.id}`}>
        <h3>{theme?.name}</h3>
      </NavLink>
      {/* Make sure description is a string, in the case of end point it is an array of goals, so we dont render it */}
      {process?.description && process?.description === String && (
        <p styleName="description">{process?.description}</p>
      )}
      <p styleName="subtitle">
        {translate('work.feedback.subtitle') as string}
      </p>
    </div>
  );
};

const TrainingEvidence = ({ evidence }: { evidence: EvidenceEntity[] }) => {
  const trainingSubscription = useRedux((state) =>
    state.trainingSubscription?.entities.allIds
      .map((id) => state.trainingSubscription?.entities.byId[id])
      ?.find((ts) =>
        ts?.attendances?.find((a) => a.id === evidence[0]?.process_id)
      )
  );

  const training = useRedux((state) =>
    trainingSubscription?.training_id
      ? state.training?.entities.byId?.[trainingSubscription?.training_id]
      : undefined
  );

  const lessonId = trainingSubscription?.attendances?.find(
    (a) => a.id === evidence[0]?.process_id
  )?.lesson_id;

  const session = training?.sessions?.find((s) =>
    s.lessons?.find((l) => l.id === lessonId)
  );

  const lesson = session?.lessons?.find((l) => l.id === lessonId);

  return (
    <div styleName="info">
      <h1>{training?.title}</h1>
      <p styleName="lesson">{lesson?.title}</p>
    </div>
  );
};

export default function EvidenceModal({
  setModalIsOpen,
  evidence,
  handleDelete,
  deleteCondition,
  initialImage,
  disableComments,
  canDeleteIfUserCreated,
  disableMessageBar,
}: Props) {
  const evidenceType = evidence[0].process_type;
  const [current, setCurrent] = useState(initialImage || 0);
  const [inputValue, setInputValue] = useState('');

  const dispatch = useReduxAction();

  const feedback = useRedux((state) =>
    state.feedback?.entities.allIds.map(
      (id) => state.feedback?.entities.byId[id]
    )
  )?.filter((fb) => fb?.evidence_id === evidence?.[current]?.id);

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();

    dispatch(
      storeFeedback({
        evidence_id: evidence?.[current]?.id,
        message: inputValue,
      })
    ).then(() => {
      setInputValue('');
    });
  }

  function deleteFeedback(id: number) {
    dispatch(deleteFeedbackAction(id));
  }

  const feedbackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (feedbackRef?.current) feedbackRef.current.scrollTop = 500000;
  }, [feedback?.length]);

  const userId = useRedux((state) => state.authentication?.user?.id);

  return (
    <Modal
      setModalIsOpen={setModalIsOpen}
      size={disableComments ? 'medium' : 'large'}
    >
      <div styleName={`wrapper ${disableComments ? 'disable-comments' : ''}`}>
        <ImageManager
          images={evidence.map(
            (e) =>
              ({
                name: e.name,
                path: e.path,
                evidenceId: e.id,
                author: e.author,
              } as EvidenceState)
          )}
          current={current}
          setCurrent={setCurrent}
          handleDelete={handleDelete ? (c) => handleDelete(c) : undefined}
          setModalIsOpen={setModalIsOpen}
          deleteCondition={deleteCondition}
          feedback={feedback}
          initialImage={initialImage}
          canDeleteIfUserCreated={
            canDeleteIfUserCreated
              ? evidence?.[current]?.created_by === userId
              : undefined
          }
          extern={evidence?.[current]?.type === 'extern'}
        />
        {!disableComments && (
          <div styleName="right">
            {userId ? (
              <>
                {evidenceType === 'App\\ProjectProcess' ? (
                  <ProjectEvidence evidence={evidence} />
                ) : (
                  <TrainingEvidence evidence={evidence} />
                )}
              </>
            ) : (
              <h2>Log in om feedback te geven</h2>
            )}
            <div styleName="feedback" ref={feedbackRef}>
              {feedback?.map(
                (fb) =>
                  !!fb && (
                    <Message
                      message={fb}
                      key={fb?.id}
                      handleDelete={deleteFeedback}
                      type="comment"
                    />
                  )
              )}
            </div>
            {!!userId && !disableMessageBar && (
              <MessageBar
                inputValue={inputValue}
                setInputValue={setInputValue}
                handleSubmit={handleSubmit}
              />
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
